import { useMutation, useQuery } from '@tanstack/react-query';
import AdminSearchResultsGrid from 'components/admin/search/AdminSearchResultsGrid';
import Tooltip from 'components/controls/Tooltip';
import FontIcon from 'components/icons/FontIcon';
import { TextButton } from 'components/v2/buttons';
import TextWithAnimatedEllipsis from 'components/v2/file-upload/TextWithAnimatedEllipsis';
import { HelpBox } from 'components/v2/help-boxes';
import { toaster } from 'components/v2/toast';
import { FileIcon } from 'images/icons/png/v2';
import { Button } from 'primereact/button';
import { Column } from 'primereact/column';
import React, { ComponentProps, useContext } from 'react';
import { EpdLinks } from 'routes/EpdRoutes';
import { QUERY_KEYS } from 'services/api/constants';
import { useArchivedVismaInvoices } from 'services/api/queries';
import { CompanyContext } from 'contexts/CompanyContextProvider';
import AuthService from 'services/AuthService';
import styled from 'styled-components';
import { HyperLink } from 'styles/Styles.styled';
import { FieldPanelFullWidth, H1NewUI } from 'styles/v2/Styles.styled';
import { FortnoxCompanyInvoice } from 'types/fortnox/FortnoxCompanyInvoice';
import { formatDate } from 'util/utils';
import {
  downloadFortnoxInvoicePreview,
  fetchCompanyInvoices,
  FortnoxCompanyInvoicesResult,
  syncCompanyInvoices,
  updateCompaniesBillingStatus,
} from '../../services/api/fortnoxApi';
interface Props {
  companyId: string;
  companyName?: string;
  accountId: string;
}

const PAGE_SIZES = [10, 50, 100];

const formatDateISO = (date?: string | null): string => {
  if (!date) return '-';
  return formatDate(date) ?? '-';
};

const formatLastUpdatedAt = (date?: string | null): string => {
  if (!date) return '-';

  const parsedDate = new Date(date);
  if (Number.isNaN(parsedDate.getTime())) return '-';

  const day = String(parsedDate.getDate()).padStart(2, '0');
  const month = String(parsedDate.getMonth() + 1).padStart(2, '0');
  const year = parsedDate.getFullYear();
  const hours = String(parsedDate.getHours()).padStart(2, '0');
  const minutes = String(parsedDate.getMinutes()).padStart(2, '0');

  return `${hours}:${minutes} ${year}-${month}-${day} UTC`;
};

const ManageInvoiceList: React.FC<Props> = ({ companyId, companyName, accountId }) => {
  const [pageIndex, setPageIndex] = React.useState(0);
  const [pageSize, setPageSize] = React.useState(PAGE_SIZES[0]);
  const [failedInvoiceDownloads, setFailedInvoiceDownloads] = React.useState<Set<string>>(new Set());
  const [loadingInvoiceDownloads, setLoadingInvoiceDownloads] = React.useState<Set<string>>(new Set());
  const isAdmin = AuthService.isAdmin();
  const { refreshCompany, changeCompany } = useContext(CompanyContext);

  const invoicesQuery = useQuery<FortnoxCompanyInvoicesResult>({
    queryKey: [QUERY_KEYS.FORTNOX_COMPANY_INVOICES, companyId, pageIndex, pageSize],
    queryFn: async () => {
      return fetchCompanyInvoices(companyId, pageIndex + 1, pageSize);
    },
    retry: false,
  });

  const { isLoading, error, data, isFetching } = invoicesQuery;

  const syncInvoicesMutation = useMutation({
    mutationFn: async () => syncCompanyInvoices(companyId),
    onSuccess: async () => {
      await invoicesQuery.refetch();
    },
  });

  const updateBillingStatusMutation = useMutation({
    mutationFn: async () => updateCompaniesBillingStatus(),
    onSuccess: async () => {
      await changeCompany(companyId);
    },
  });

  const { data: archivedInvoicesSummary } = useArchivedVismaInvoices(accountId);

  const hasArchivedInvoices =
    (archivedInvoicesSummary?.drafts?.length ?? 0) > 0 || (archivedInvoicesSummary?.invoices?.length ?? 0) > 0;

  const viewArchivedInvoices = () => {
    const newInvoicesCount = archivedInvoicesSummary?.drafts?.length ?? 0;
    const allInvoicesCount = newInvoicesCount + (archivedInvoicesSummary?.invoices?.length ?? 0);

    if (newInvoicesCount === 1 && archivedInvoicesSummary?.drafts?.[0].invoiceId) {
      window.open(
        EpdLinks.invoice(companyId, accountId, archivedInvoicesSummary.drafts[0].invoiceId),
        '_blank',
        'noopener,noreferrer'
      );
    } else if (allInvoicesCount === 1 && newInvoicesCount === 0 && archivedInvoicesSummary?.invoices?.[0].invoiceId) {
      window.open(
        EpdLinks.invoice(companyId, accountId, archivedInvoicesSummary.invoices[0].invoiceId),
        '_blank',
        'noopener,noreferrer'
      );
    } else {
      window.open(EpdLinks.invoices(companyId, accountId), '_blank', 'noopener,noreferrer');
    }
  };

  const archivedInvoicesButton = hasArchivedInvoices ? (
    <span>
      <TextButton
        type="button"
        style={{ fontSize: 14, fontWeight: 500, padding: 0 }}
        text
        label="Archived invoices"
        icon={<FontIcon icon="archiveInvoice" style={{ fontSize: '12px', marginRight: '5px', marginTop: '0.1rem' }} />}
        onClick={() => viewArchivedInvoices()}
      />
      <Tooltip position="bottom">Invoices created before April 2024 are archived here.</Tooltip>
    </span>
  ) : null;

  const handleOnPageChange: ComponentProps<typeof AdminSearchResultsGrid>['onPageChange'] = (event) => {
    setPageIndex(event.pageIndex);
    setPageSize(event.pageSize);
  };

  if (isLoading) {
    return (
      <Container>
        <TextWithAnimatedEllipsis>Loading invoices</TextWithAnimatedEllipsis>
      </Container>
    );
  }

  if (error) {
    const message = error instanceof Error ? error.message : 'Unknown error';
    return (
      <Container>
        <FieldPanelFullWidth>Failed to load invoices: {message}</FieldPanelFullWidth>
      </Container>
    );
  }
  if (!data) {
    return null;
  }

  const lastUpdatedText = `Last update was performed at ${formatLastUpdatedAt(data.lastSyncedAt)}`;

  if (data.totalCount === 0) {
    return (
      <Container>
        <InvoiceLastSyncHelpBox>
          {lastUpdatedText} {archivedInvoicesButton}
        </InvoiceLastSyncHelpBox>
        {isAdmin && (
          <ActionsRow>
            <Button
              label="Sync now"
              onClick={() => syncInvoicesMutation.mutate()}
              loading={syncInvoicesMutation.isPending}
              disabled={syncInvoicesMutation.isPending}
            />
            {AuthService.isSystemAdmin() && (
              <Button
                label="Update billing state"
                severity="secondary"
                onClick={() => updateBillingStatusMutation.mutate()}
                loading={updateBillingStatusMutation.isPending}
                disabled={updateBillingStatusMutation.isPending}
                style={{ marginLeft: '1rem' }}
              />
            )}
          </ActionsRow>
        )}
        <EmptyInvoicesList />
      </Container>
    );
  }

  const loadInvoice = async (invoice: FortnoxCompanyInvoice): Promise<void> => {
    if (!invoice.invoiceNumber) return;

    const loadingStartTime = Date.now();
    const minLoadingDurationMs = 1000;

    setLoadingInvoiceDownloads((prev) => new Set(prev).add(invoice.invoiceNumber));

    try {
      await downloadFortnoxInvoicePreview(companyId, invoice.invoiceNumber, invoice.dateOfIssue, companyName);

      setFailedInvoiceDownloads((prev) => {
        const next = new Set(prev);
        next.delete(invoice.invoiceNumber);
        return next;
      });
    } catch {
      toaster({
        severity: 'error',
        summary: 'Invoice download failed.',
        details: (
          <span>
            Please request a copy at <HyperLink href="mailto:finance@environdec.com">finance@environdec.com</HyperLink>.
          </span>
        ),
        options: { autoClose: false, closeOnClick: false },
      });
      setFailedInvoiceDownloads((prev) => new Set(prev).add(invoice.invoiceNumber));
    } finally {
      const elapsedMs = Date.now() - loadingStartTime;
      if (elapsedMs < minLoadingDurationMs) {
        await new Promise((resolve) => window.setTimeout(resolve, minLoadingDurationMs - elapsedMs));
      }

      setLoadingInvoiceDownloads((prev) => {
        const next = new Set(prev);
        next.delete(invoice.invoiceNumber);
        return next;
      });
    }
  };

  return (
    <Container>
      <InvoiceLastSyncHelpBox>
        {lastUpdatedText} {archivedInvoicesButton}
      </InvoiceLastSyncHelpBox>
      <HeaderRow>
        <H1NewUI>Your invoices</H1NewUI>
        {isAdmin && (
          <ActionsRow>
            <Button
              label="Sync now"
              onClick={() => syncInvoicesMutation.mutate()}
              loading={syncInvoicesMutation.isPending}
              disabled={syncInvoicesMutation.isPending}
            />
            {AuthService.isSystemAdmin() && (
              <Button
                label="Update billing state"
                severity="secondary"
                onClick={() => updateBillingStatusMutation.mutate()}
                loading={updateBillingStatusMutation.isPending}
                disabled={updateBillingStatusMutation.isPending}
                style={{ marginLeft: '1rem' }}
              />
            )}
          </ActionsRow>
        )}
      </HeaderRow>
      <InvoiceSearchResultsGrid
        value={data.items}
        totalRecords={data.totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPageChange={handleOnPageChange}
        pageSizeOptions={PAGE_SIZES}
        loading={isFetching}
        tableStyle={{ tableLayout: 'auto' }}
      >
        <Column
          field="invoiceNumber"
          header="Invoice number"
          style={{ width: 150 }}
          body={(item: FortnoxCompanyInvoice) => (
            <EllipsedCell>
              <EllipsedText>{item.invoiceNumber || '-'}</EllipsedText>
            </EllipsedCell>
          )}
        />
        <Column
          field="dateOfIssue"
          header="Date of issue"
          style={{ width: 130 }}
          body={(item: FortnoxCompanyInvoice) => (
            <EllipsedCell>
              <EllipsedText>{formatDateISO(item.dateOfIssue)}</EllipsedText>
            </EllipsedCell>
          )}
        />
        <Column
          field="dueDate"
          header="Due date"
          style={{ width: 130 }}
          body={(item: FortnoxCompanyInvoice) => (
            <EllipsedCell>
              <EllipsedText>{formatDateISO(item.dueDate)}</EllipsedText>
            </EllipsedCell>
          )}
        />
        <Column
          field="amount"
          align={'right'}
          header="Amount"
          style={{ width: 120 }}
          body={(item: FortnoxCompanyInvoice) => (
            <EllipsedCell style={{ justifyContent: 'flex-end' }}>
              <EllipsedText>{item.amount || '-'}</EllipsedText>
            </EllipsedCell>
          )}
        />
        <Column
          field="currency"
          header="Currency"
          style={{ width: 100 }}
          body={(item: FortnoxCompanyInvoice) => (
            <EllipsedCell>
              <EllipsedText>{item.currency || '-'}</EllipsedText>
            </EllipsedCell>
          )}
        />
        <Column
          field="invoiceStatus"
          header="Status"
          style={{ width: 120 }}
          body={(item: FortnoxCompanyInvoice) => (
            <EllipsedCell>
              <StatusBadge status={item.invoiceStatus}>{item.invoiceStatus || '-'}</StatusBadge>
            </EllipsedCell>
          )}
        />
        <Column
          style={{ width: 80 }}
          body={(item: FortnoxCompanyInvoice) => {
            const isDownloadUnavailable = !item.invoiceNumber || failedInvoiceDownloads.has(item.invoiceNumber);
            const isDownloadLoading = !!item.invoiceNumber && loadingInvoiceDownloads.has(item.invoiceNumber);

            return (
              <DownloadCell>
                <TextButton
                  text
                  icon={<FontIcon icon="download" style={{ fontSize: 24 }} />}
                  onClick={() => loadInvoice(item)}
                  disabled={isDownloadUnavailable || isDownloadLoading}
                  loading={isDownloadLoading}
                />
              </DownloadCell>
            );
          }}
        />
      </InvoiceSearchResultsGrid>
    </Container>
  );
};

const EmptyInvoicesList = () => {
  return (
    <EmptyBox>
      <div style={{ display: 'flex', flexDirection: 'row', alignSelf: 'center' }}>
        <IconBox>
          <img src={FileIcon} alt="file icon" height={158} width={158} />
        </IconBox>
        <div style={{ display: 'flex', flexDirection: 'column', alignSelf: 'center' }}>
          <NoInvoicesText>No invoices found</NoInvoicesText>
          <InformativeText>Once invoices are available, they will be displayed here</InformativeText>
        </div>
      </div>
    </EmptyBox>
  );
};

export default ManageInvoiceList;

const InvoiceLastSyncHelpBox = styled(HelpBox)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: row;
`;

const ArchiveInvoiceIcon = styled.img`
  width: 12px;
  height: 12px;
  margin: 0 5px 0 0;
  cursor: pointer;
`;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const InvoiceSearchResultsGrid = styled(AdminSearchResultsGrid)`
  box-shadow: none;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ActionsRow = styled.div`
  display: flex;
  justify-content: flex-end;
`;

const DownloadCell = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  max-height: 1rem;
`;

const EllipsedCell = styled.div`
  display: flex;
  align-items: center;
  line-height: 1.2;
`;

const EllipsedText = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  flex-shrink: 1;
`;

const getStatusColors = (status: string, theme: any) => {
  const normalizedStatus = status?.toLowerCase();

  const statusColorMap = {
    paid: {
      bg: theme.colors.statusLabelGreenBg,
      text: theme.colors.statusLabelGreenText,
    },
    unpaid: {
      bg: '#E0E0E0',
      text: '#666666',
    },
    overdue: {
      bg: theme.colors.statusLabelRedBg,
      text: theme.colors.statusLabelRedText,
    },
    default: {
      bg: theme.colors.statusLabelYellowBg,
      text: theme.colors.statusLabelYellowText,
    },
  };

  return statusColorMap[normalizedStatus as keyof typeof statusColorMap] || statusColorMap.default;
};

const StatusBadge = styled.span<{ status: string }>`
  padding: 4px 12px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
  background-color: ${(props) => getStatusColors(props.status, props.theme).bg};
  color: ${(props) => getStatusColors(props.status, props.theme).text};
`;

const EmptyBox = styled.div`
  width: 100%;
  height: 400px;
  margin: auto;
  border-radius: 4px;
  padding-left: 3rem;
  padding-right: 3rem;
  padding-top: 0rem;
  padding-bottom: 2rem;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;

  > div {
    flex: 1;
  }
`;

const IconBox = styled.div`
  width: 158px;
  height: 158px;
  margin: auto;
  align-self: flex-start;
`;

const NoInvoicesText = styled.h2`
  ${(props) => props.theme.typography.bodyInput}
  color: ${(props) => props.theme.colors.primaryBlack};
  font-size: 18px;
  font-weight: 600;
`;

const InformativeText = styled.span`
  ${(props) => props.theme.typography.bodyInput}
  font-weight: 500;
  color: ${(props) => props.theme.colors.primaryBlack50};
`;
